/**
 * SRT / WebVTT file helpers for Clipping Studio import/export.
 * SRT: index line, "HH:MM:SS,mmm --> HH:MM:SS,mmm", text, blank line (comma in timestamps).
 * WebVTT export mirrors backend SubtitlesService.ConvertSubtitleUrlToVTT (dot in timestamps).
 * WebVTT import mirrors packaging WebVttPeriodSlicer.ParseCues (header/NOTE/STYLE, optional cue id).
 * Import skips blank lines between the timestamp and cue text; export trims cue text.
 */

export interface SrtCue {
  index: number;
  startMs: number;
  endMs: number;
  text: string;
}

/** Shared cue shape for formatSrt / formatVtt. */
export interface SubtitleFileCue {
  inMs: number;
  outMs: number;
  text: string;
}

const SRT_TIMECODE =
  /^(\d{2}):(\d{2}):(\d{2})[,.](\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2})[,.](\d{3})/;

/** HH:MM:SS.mmm or MM:SS.mmm (comma or dot); optional cue settings after end time. */
const VTT_TIMESTAMP_LINE =
  /^(\d{1,2}:\d{2}:\d{2}[.,]\d{1,3}|\d{1,2}:\d{2}[.,]\d{1,3})\s*-->\s*(\d{1,2}:\d{2}:\d{2}[.,]\d{1,3}|\d{1,2}:\d{2}[.,]\d{1,3})(?:\s+.*)?$/;

function parseSrtTimecode(
  h: number,
  m: number,
  s: number,
  ms: number,
): number {
  return h * 3600000 + m * 60000 + s * 1000 + ms;
}

/** Parse a WebVTT timestamp fragment into milliseconds. */
function parseVttTimecode(raw: string): number {
  const normalized = raw.trim().replace(",", ".");
  const parts = normalized.split(":");
  if (parts.length === 3) {
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const secParts = parts[2].split(".");
    const s = parseInt(secParts[0], 10);
    const frac = (secParts[1] ?? "0").padEnd(3, "0").slice(0, 3);
    return parseSrtTimecode(h, m, s, parseInt(frac, 10));
  }
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10);
    const secParts = parts[1].split(".");
    const s = parseInt(secParts[0], 10);
    const frac = (secParts[1] ?? "0").padEnd(3, "0").slice(0, 3);
    return parseSrtTimecode(0, m, s, parseInt(frac, 10));
  }
  return 0;
}

export function parseSrt(content: string): SrtCue[] {
  const cues: SrtCue[] = [];
  const lines = content.split(/\r?\n/);
  let i = 0;

  while (i < lines.length) {
    const indexLine = lines[i].trim();
    if (!indexLine) {
      i++;
      continue;
    }

    const index = parseInt(indexLine, 10);
    if (Number.isNaN(index)) {
      i++;
      continue;
    }

    i++;
    if (i >= lines.length) break;

    const timeLine = lines[i].trim();
    const timeMatch = timeLine.match(SRT_TIMECODE);
    if (!timeMatch) {
      i++;
      continue;
    }

    const startMs = parseSrtTimecode(
      parseInt(timeMatch[1], 10),
      parseInt(timeMatch[2], 10),
      parseInt(timeMatch[3], 10),
      parseInt(timeMatch[4], 10),
    );
    const endMs = parseSrtTimecode(
      parseInt(timeMatch[5], 10),
      parseInt(timeMatch[6], 10),
      parseInt(timeMatch[7], 10),
      parseInt(timeMatch[8], 10),
    );
    i++;

    while (i < lines.length && lines[i].trim() === "") {
      i++;
    }

    const textLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== "") {
      textLines.push(lines[i].trim());
      i++;
    }
    const text = textLines.join("\n").trim();

    cues.push({ index, startMs, endMs, text });
    i++;
  }

  return cues;
}

/**
 * Parse WebVTT file content into cues (same shape as parseSrt for import mapping).
 * Skips WEBVTT header, NOTE/STYLE/REGION lines; supports optional cue identifiers.
 * Tolerates blank lines between the timestamp and the cue payload.
 */
export function parseVtt(content: string): SrtCue[] {
  const cues: SrtCue[] = [];
  const lines = (content ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");
  let i = 0;
  let index = 1;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (
      !trimmed ||
      trimmed.toUpperCase().startsWith("WEBVTT") ||
      trimmed.toUpperCase().startsWith("NOTE") ||
      trimmed.toUpperCase().startsWith("STYLE") ||
      trimmed.toUpperCase().startsWith("REGION")
    ) {
      i++;
      continue;
    }

    let timeMatch = trimmed.match(VTT_TIMESTAMP_LINE);
    if (!timeMatch && i + 1 < lines.length) {
      const nextTrimmed = lines[i + 1].trim();
      const nextMatch = nextTrimmed.match(VTT_TIMESTAMP_LINE);
      if (nextMatch) {
        i++;
        timeMatch = nextMatch;
      }
    }

    if (!timeMatch) {
      i++;
      continue;
    }

    const startMs = parseVttTimecode(timeMatch[1]);
    const endMs = parseVttTimecode(timeMatch[2]);
    i++;

    while (i < lines.length && lines[i].trim() === "") {
      i++;
    }

    const textLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !VTT_TIMESTAMP_LINE.test(lines[i].trim())
    ) {
      textLines.push(lines[i]);
      i++;
    }
    const text = textLines.join("\n").trim();

    cues.push({ index, startMs, endMs, text });
    index++;

    while (i < lines.length && lines[i].trim() === "") {
      i++;
    }
  }

  return cues;
}

function padTimePart(n: number, len = 2): string {
  return n.toString().padStart(len, "0");
}

function msToSrtTime(totalMs: number): string {
  const ms = Math.floor(Math.max(0, totalMs));
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const frac = Math.floor(ms % 1000);
  return `${padTimePart(h)}:${padTimePart(m)}:${padTimePart(s)},${padTimePart(frac, 3)}`;
}

/** WebVTT timestamp HH:MM:SS.mmm (dot decimal), same as backend MsToVttTime. */
function msToVttTime(totalMs: number): string {
  const ms = Math.floor(Math.max(0, totalMs));
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const frac = Math.floor(ms % 1000);
  return `${padTimePart(h)}:${padTimePart(m)}:${padTimePart(s)}.${padTimePart(frac, 3)}`;
}

function normalizeCueText(text: string): string {
  return (text ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
}

/** Format a single subtitle cue as SRT (index, timecode, text). */
function formatSrtCue(index: number, startMs: number, endMs: number, text: string): string {
  return `${index}\n${msToSrtTime(startMs)} --> ${msToSrtTime(endMs)}\n${normalizeCueText(text)}\n`;
}

/** Convert subtitle events to SRT file content. */
export function formatSrt(subtitles: SubtitleFileCue[]): string {
  if (!subtitles.length) return "";
  return subtitles
    .map((s, i) => formatSrtCue(i + 1, s.inMs, s.outMs, s.text))
    .join("\n");
}

/**
 * Convert subtitle events to WebVTT file content.
 * Parity with Infrastructure SubtitlesService.ConvertSubtitleUrlToVTT (no cue indices).
 */
export function formatVtt(subtitles: SubtitleFileCue[]): string {
  if (!subtitles.length) return "";
  const lines: string[] = ["WEBVTT", ""];
  for (const s of subtitles) {
    lines.push(`${msToVttTime(s.inMs)} --> ${msToVttTime(s.outMs)}`);
    lines.push(normalizeCueText(s.text));
    lines.push("");
  }
  return lines.join("\n");
}
